import { useMemo, useState } from 'react'
import { Plus, Receipt, TriangleAlert } from 'lucide-react'

import {
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_VARIANT,
  PAYMENT_METHOD_LABELS,
  type Invoice,
  type InvoiceItemCode,
  type InvoiceLineBody,
  type PaymentMethod,
} from '@/types/finance'
import {
  useCreateInvoice,
  useInvoice,
  useInvoices,
  useOpenInvoice,
  useRecordPayment,
  useStorageInvoicingStatus,
  useVoidInvoice,
} from '@/features/finance/hooks'
import { useCustomers } from '@/features/customers/hooks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const today = () => new Date().toISOString().slice(0, 10)

// InvoicesTab is the AR surface: the invoice register (all sources —
// generated storage/work-order/boat-sale invoices plus manual drafts),
// payment recording, and voiding. Sync to QuickBooks happens automatically
// when an invoice opens.
export function InvoicesTab() {
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')
  const { data: invoices = [], isLoading } = useInvoices({
    status: statusFilter || undefined,
    q: search || undefined,
  })
  const { data: storageStatus } = useStorageInvoicingStatus()

  const [creating, setCreating] = useState(false)
  const [detail, setDetail] = useState<Invoice | null>(null)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search number or customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <Select
            value={statusFilter === '' ? 'all' : statusFilter}
            onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {(Object.keys(INVOICE_STATUS_LABELS) as (keyof typeof INVOICE_STATUS_LABELS)[]).map(
                (s) => (
                  <SelectItem key={s} value={s}>
                    {INVOICE_STATUS_LABELS[s]}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          {storageStatus && storageStatus.needs_payer_count > 0 && (
            <Badge variant="outline" className="gap-1 text-amber-600">
              <TriangleAlert className="h-3 w-3" />
              {storageStatus.needs_payer_count} storage allocation
              {storageStatus.needs_payer_count === 1 ? '' : 's'} need a payer
            </Badge>
          )}
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" /> New invoice
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Number</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground">
                  No invoices yet — storage, work-order, and boat-sale invoices
                  generate automatically; drafts can be created manually.
                </TableCell>
              </TableRow>
            )}
            {invoices.map((inv) => (
              <TableRow
                key={inv.id}
                className="cursor-pointer"
                onClick={() => setDetail(inv)}
              >
                <TableCell className="font-medium">{inv.number}</TableCell>
                <TableCell>{inv.customer_name}</TableCell>
                <TableCell>{inv.issued_on}</TableCell>
                <TableCell>
                  {inv.due_on}
                  {inv.overdue && (
                    <Badge variant="destructive" className="ml-2">
                      Overdue
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={INVOICE_STATUS_VARIANT[inv.status]}>
                    {INVOICE_STATUS_LABELS[inv.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">{money(inv.total)}</TableCell>
                <TableCell className="text-right">{money(inv.balance_due)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <NewInvoiceDialog open={creating} onOpenChange={setCreating} />
      <InvoiceDetailDialog invoice={detail} onClose={() => setDetail(null)} />
    </div>
  )
}

// --- New manual invoice (draft) ---

const LINE_ITEM_CODES: { value: InvoiceItemCode; label: string }[] = [
  { value: 'service_labor', label: 'Service labor' },
  { value: 'part', label: 'Part' },
  { value: 'other', label: 'Other' },
]

function NewInvoiceDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const create = useCreateInvoice()
  const { data: customers = [] } = useCustomers()
  const [customerId, setCustomerId] = useState('')
  const [notes, setNotes] = useState('')
  const [dueOn, setDueOn] = useState('')
  const [lines, setLines] = useState<InvoiceLineBody[]>([
    { item_code: 'service_labor', description: '', quantity: 1, unit_price: 0 },
  ])

  const total = useMemo(
    () => lines.reduce((sum, l) => sum + l.quantity * l.unit_price, 0),
    [lines],
  )

  const reset = () => {
    setCustomerId('')
    setNotes('')
    setDueOn('')
    setLines([{ item_code: 'service_labor', description: '', quantity: 1, unit_price: 0 }])
  }

  const submit = async () => {
    if (!customerId) return
    try {
      await create.mutateAsync({
        customer_id: customerId,
        notes: notes || undefined,
        due_on: dueOn || undefined,
        lines: lines.filter((l) => l.description.trim() !== ''),
      })
      reset()
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
          <DialogDescription>
            Created as a draft — open it to start the QuickBooks sync.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-2">
            <Label>Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a customer" />
              </SelectTrigger>
              <SelectContent>
                {customers.map((c) => {
                  const name =
                    [c.first_name, c.last_name].filter(Boolean).join(' ') ||
                    c.company_name ||
                    c.id
                  return (
                    <SelectItem key={c.id} value={c.id}>
                      {name}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Lines</Label>
            {lines.map((line, idx) => (
              <div key={idx} className="flex gap-2">
                <Select
                  value={line.item_code}
                  onValueChange={(v) =>
                    setLines((ls) =>
                      ls.map((l, i) =>
                        i === idx ? { ...l, item_code: v as InvoiceItemCode } : l,
                      ),
                    )
                  }
                >
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINE_ITEM_CODES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Description"
                  value={line.description}
                  onChange={(e) =>
                    setLines((ls) =>
                      ls.map((l, i) => (i === idx ? { ...l, description: e.target.value } : l)),
                    )
                  }
                  className="flex-1"
                />
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="Qty"
                  value={line.quantity}
                  onChange={(e) =>
                    setLines((ls) =>
                      ls.map((l, i) =>
                        i === idx ? { ...l, quantity: Number(e.target.value) || 0 } : l,
                      ),
                    )
                  }
                  className="w-20"
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Price"
                  value={line.unit_price}
                  onChange={(e) =>
                    setLines((ls) =>
                      ls.map((l, i) =>
                        i === idx ? { ...l, unit_price: Number(e.target.value) || 0 } : l,
                      ),
                    )
                  }
                  className="w-28"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                  disabled={lines.length === 1}
                >
                  ✕
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setLines((ls) => [
                  ...ls,
                  { item_code: 'other', description: '', quantity: 1, unit_price: 0 },
                ])
              }
            >
              <Plus className="mr-1 h-3 w-3" /> Add line
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-2">
              <Label>Due date (defaults to Net 30)</Label>
              <Input type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>
          <div className="text-right font-medium">Total: {money(total)}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!customerId || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Invoice detail: lines, payments, actions ---

function InvoiceDetailDialog({
  invoice,
  onClose,
}: {
  invoice: Invoice | null
  onClose: () => void
}) {
  const openMutation = useOpenInvoice()
  const voidMutation = useVoidInvoice()
  // List rows don't carry lines/payments — fetch the full document.
  const { data: full } = useInvoice(invoice?.id ?? '')
  const detail = full ?? invoice

  if (!invoice || !detail) return null
  const canOpen = detail.status === 'draft'
  const canVoid = detail.status === 'draft' || detail.status === 'open'
  const canPay = detail.status === 'open'

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" /> {detail.number}
            <Badge variant={INVOICE_STATUS_VARIANT[detail.status]}>
              {INVOICE_STATUS_LABELS[detail.status]}
            </Badge>
            {detail.overdue && <Badge variant="destructive">Overdue</Badge>}
          </DialogTitle>
          <DialogDescription>
            {detail.customer_name} · issued {detail.issued_on}
            {detail.due_on ? ` · due ${detail.due_on}` : ''} ·{' '}
            {detail.source === 'manual' ? 'manual' : `auto (${detail.source})`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.lines.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>
                      {l.description || l.item_label}
                      <span className="text-muted-foreground ml-2 text-xs">{l.item_label}</span>
                    </TableCell>
                    <TableCell className="text-right">{l.quantity}</TableCell>
                    <TableCell className="text-right">{money(l.unit_price)}</TableCell>
                    <TableCell className="text-right">{money(l.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="text-right text-sm">
            <div>
              Paid: <span className="font-medium">{money(detail.amount_paid)}</span>
            </div>
            <div>
              Balance due:{' '}
              <span className="font-medium">{money(detail.balance_due)}</span>
            </div>
          </div>

          {detail.payments.length > 0 && (
            <div>
              <Label className="mb-1 block">Payments</Label>
              <div className="space-y-1 text-sm">
                {detail.payments.map((p) => (
                  <div key={p.id} className="flex justify-between text-muted-foreground">
                    <span>
                      {p.received_on} · {PAYMENT_METHOD_LABELS[p.method]}
                      {p.reference ? ` · ${p.reference}` : ''}
                    </span>
                    <span>{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {canPay && <RecordPaymentForm invoiceId={detail.id} balance={detail.balance_due} />}
        </div>

        <DialogFooter>
          {canOpen && (
            <Button
              onClick={() => openMutation.mutate(detail.id)}
              disabled={openMutation.isPending}
            >
              Open (starts QuickBooks sync)
            </Button>
          )}
          {canVoid && (
            <Button
              variant="destructive"
              onClick={() => {
                if (window.confirm(`Void invoice ${detail.number}? This cannot be undone.`)) {
                  voidMutation.mutate(detail.id, { onSuccess: onClose })
                }
              }}
              disabled={voidMutation.isPending}
            >
              Void
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function RecordPaymentForm({ invoiceId, balance }: { invoiceId: string; balance: number }) {
  const record = useRecordPayment()
  const [amount, setAmount] = useState(String(balance))
  const [method, setMethod] = useState<PaymentMethod>('check')
  const [receivedOn, setReceivedOn] = useState(today())
  const [reference, setReference] = useState('')

  const submit = async () => {
    const value = Number(amount)
    if (!value || value <= 0) return
    try {
      await record.mutateAsync({
        id: invoiceId,
        body: {
          amount: value,
          received_on: receivedOn,
          method,
          reference: reference || undefined,
        },
      })
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  return (
    <div className="space-y-2 rounded-md border p-3">
      <Label>Record a payment</Label>
      <div className="flex flex-wrap gap-2">
        <Input
          type="number"
          min="0"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-28"
        />
        <Select value={method} onValueChange={(v) => setMethod(v as PaymentMethod)}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((m) => (
              <SelectItem key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={receivedOn}
          onChange={(e) => setReceivedOn(e.target.value)}
          className="w-40"
        />
        <Input
          placeholder="Reference (check #, ACH id…)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          className="flex-1"
        />
        <Button onClick={submit} disabled={record.isPending}>
          {record.isPending ? 'Recording…' : 'Record'}
        </Button>
      </div>
    </div>
  )
}
